import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { isEqual } from 'lodash-es'
import {
    Dispatch,
    memo,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { openMenuDialog } from '../../../actions/menu'
import { openRoom, setLoungeSurrogate } from '../../../actions/room'
import { ListType } from '../../../constants/enums'
import { FloaterSize } from '../../../constants/floaters'
import {
    isLounge,
    isPreviewAvailable,
} from '../../../mfcLogic/userProperties/user'
import { openQuickMenu } from '../../../state/modals/state.confirmation'
import {
    openQuickReply,
    setFloatingPreview,
} from '../../../state/modals/state.floater'
import { idToSlug } from '../../../state/state.users'
import { msTimestamp } from '../../../utilities/dates'
import i18n from '../../../utilities/i18n'
import { TransitionTypes } from '../../../utilities/transitions'
import ExpandCollapseButton from '../../Buttons/ButtonExpandCollapse'
import IconButton from '../../Buttons/IconButton'
import BadgeAvatar from '../../common/BadgeAvatar'
import { useList } from '../../Hooks/useList'
import { useModelColor } from '../../Hooks/useModelColor'
import { usePassive } from '../../Hooks/usePassive'
import { usePMSelector } from '../../Hooks/usePM'
import { usePinnedRooms } from '../../Hooks/useRoom'
import { useRooms } from '../../Hooks/useRooms'
import { useScreenStateSelector } from '../../Hooks/useScreenState'
import { useUserSelector } from '../../Hooks/useUser'
import MessageIcon from '../../Icons/Message'
import { getThemeSpacing } from '../../styles/appTheme'
import { palette } from '../../styles/colors'
// import { NO_SCROLLBAR_STYLES } from '../../theme'
import { VList } from 'virtua'
import { getScreenState } from '../../../app-state/state.screen'
import { closeFloatingPreview } from '../../../state/modals'
import { AppRouter } from '../../../state/state.router'
import { useRouteSelector } from '../../Hooks/useRoute'
import Notification from '../Notification'
import { broadcastStateText, IMessage, messageUtil } from './utilities'

const AVATAR_SIZE = 26
const ROW_HEIGHT = AVATAR_SIZE + 8
const RECENT_MESSAGE_THRESHOLD = 12 * 60 * 60 * 1000 // 12 hours

const ConnectionsRoot = styled('div')({
    width: '100%',
    height: '100%',
    fontSize: 12,
})
const RowDiv = styled('div')({
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderLeft: '0 !important',
    borderRight: '0 !important',
    borderBottom: 'none',
})
const ResultRow = styled('div')({
    alignItems: 'center',
    display: 'flex',
    width: '100%',
    padding: 0,
    margin: '4px 0',
    cursor: 'pointer',
    overflow: 'hidden',
})
const Username = styled('div')({
    fontSize: 13,
    lineHeight: '15px',
    textOverflow: 'ellipsis',
})
const BroadcastStatus = styled('div')({
    fontFamily: 'Montserrat',
    fontSize: 9,
    fontWeight: 400,
    lineHeight: '11px',
})
const MessageBubbleContainer = styled('div')({
    display: 'flex',
    flexShrink: 0,
    position: 'relative',
})
const subtitleStyles = css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: getThemeSpacing(1.25),
    fontSize: 11,
    fontWeight: 400,
    color: palette.secondaryText,
})
// const fixedSizedListStyles = css(NO_SCROLLBAR_STYLES)
const resetExpandPosition = css({ top: 0 })
const messageBubbleStyles = css({
    fontWeight: 'bold',
    fontSize: 10,
    padding: '3px 4px',
    '& svg': {
        color: palette.greenMFC,
    },
})
const notificationStyles = css({
    position: 'absolute' as const,
    top: 4,
    right: 6,
    backgroundColor: 'transparent',
    pointerEvents: 'none',
})

type SectionType =
    | 'openRooms'
    | 'recentMessages'
    | 'bookmarkedFriends'
    | 'modelFriends'
    | 'memberFriends'
    | 'bookmarks'

interface OpenRoom {
    type: 'openRooms'
    id: number
    unreadMessages: number
}
interface RecentMessage {
    type: 'recentMessages'
    slug: string
    unreadMessages: number
}
interface SectionEntry {
    type: SectionType
    id: number
}

type Item = Section | SectionEntry | OpenRoom | RecentMessage

interface Section {
    key: SectionType
    list: Array<SectionEntry | OpenRoom | RecentMessage>
    length: number
    isExpanded: boolean
    setIsExpanded: Dispatch<SetStateAction<boolean>>
    type?: string
}

const isSection = (item: Item): item is Section =>
    typeof (item as Section)?.key === 'string'

const isOpenRoom = (item: Item): item is OpenRoom =>
    (item as OpenRoom)?.type === 'openRooms'

const isRecentMessage = (item: Item): item is RecentMessage =>
    (item as RecentMessage)?.type === 'recentMessages'

const isSectionEntry = (item: Item): item is SectionEntry =>
    typeof (item as SectionEntry)?.id === 'number'

type List = Array<Item>

const itemKeySelector = (index: number, item: Item) => {
    if (isSectionEntry(item)) return `${item.type}-${item.id}`
    if (isRecentMessage(item)) return `${item.type}-${item.slug}`
    if (isSection(item)) return item.key
    return index
}

interface RowProps {
    item: Item
    passiveMode: boolean
    loungeSlug: string | undefined
    openMenu(slug: string): void
    setPreview: (show: boolean, id?: number, x?: number, y?: number) => void
}

const Row = memo((props: RowProps) => {
    const avatarRef = useRef<HTMLElement>(null)
    const { item, passiveMode, loungeSlug, openMenu, setPreview } = props
    const itemId = isSectionEntry(item) ? item.id : undefined
    const itemIsRecentMessage = isRecentMessage(item)
    const { key, length, isExpanded, setIsExpanded } = isSection(item)
        ? item
        : ({} as Section)
    const { slug: entrySlug } = itemIsRecentMessage
        ? item
        : ({} as RecentMessage)
    const isOpenedRoom = isOpenRoom(item)
    const { unreadMessages } =
        isOpenedRoom || itemIsRecentMessage
            ? item
            : ({} as OpenRoom | RecentMessage)
    const identifier = itemId || entrySlug
    const id = useUserSelector(identifier, 'id', { lookup: true })
    const isBroadcasting = useUserSelector(identifier, 'isBroadcasting')
    const isOffline = useUserSelector(identifier, 'isOffline')
    const isLounge = useUserSelector(identifier, 'isLounge')
    const isModel = useUserSelector(identifier, 'isModel')
    const isAway = useUserSelector(identifier, 'isAway')
    const isPublicShow = useUserSelector(identifier, 'isPublicShow')
    const isTruePrivate = useUserSelector(identifier, 'isTruePrivate')
    const isPrivateShow = useUserSelector(identifier, 'isPrivateShow')
    const isGroupShow = useUserSelector(identifier, 'isGroupShow')
    const isClubShow = useUserSelector(identifier, 'isClubShow')
    const banned = useUserSelector(identifier, 'banned')
    const color = useUserSelector(identifier, 'color')
    const slug = useUserSelector(identifier, 'slug')
    const name = useUserSelector(identifier, 'name')

    const previewAvailable = isPreviewAvailable({
        isAway,
        isBroadcasting,
        isPublicShow,
        banned,
    })
    const canShowPreview = !passiveMode && previewAvailable
    const modelColor = useModelColor(color)

    const handleClick = useCallback(() => {
        if (loungeSlug && previewAvailable && isModel) {
            // if we're in the lounge and clicking on a model that is publicly
            // broadcasting, set the surrogate
            setLoungeSurrogate(id, loungeSlug)
        } else if (loungeSlug && isModel) {
            // if we're in the lounge and its a model that we can't show
            // (not broadcasting, or one of the various non-public show types)
            openMenu(slug)
        } else if (itemIsRecentMessage) {
            // if we clicked on one of the recent messages
            if (isOpenedRoom) openQuickReply(slug, { size: FloaterSize.Small })
            else openQuickReply(slug)
        } else if (isModel) {
            // if its a model when we arent in the lounge
            openRoom(slug, {
                transitionSource: TransitionTypes.SIDEBAR,
            })
        } else {
            // if its not a model
            if (isOpenedRoom) openQuickReply(slug, { size: FloaterSize.Small })
            else openQuickReply(slug)
        }
    }, [
        id,
        slug,
        isModel,
        loungeSlug,
        previewAvailable,
        itemIsRecentMessage,
        isOpenedRoom,
    ])

    const onOpenMenu = useCallback(
        (e) => {
            // close the hover preview if we click the avatar
            setPreview(false)
            if (isModel) {
                openMenu(slug)
            } else {
                openQuickMenu(id, {
                    x: e.clientX,
                    y: e.clientY,
                })
            }
        },
        [openMenu, slug, isModel],
    )

    const onOpenQuickReply = useCallback(() => {
        if (isOpenedRoom) openQuickReply(slug, { size: FloaterSize.Small })
        else openQuickReply(slug)
    }, [slug, isOpenedRoom])

    const onMouseEnter = useCallback(() => {
        if (canShowPreview) {
            const { x, y } = avatarRef.current.getBoundingClientRect()
            setPreview(true, id, x, y)
        }
    }, [canShowPreview, id, setPreview])

    const onMouseLeave = useCallback(() => {
        if (canShowPreview) {
            setPreview(false, id)
        }
    }, [canShowPreview, id, setPreview])

    if (key) {
        return (
            <div css={subtitleStyles}>
                {i18n.t(`friends.${key}`)} ({length})
                <ExpandCollapseButton
                    styleOverrides={resetExpandPosition}
                    isExpanded={isExpanded}
                    setIsExpanded={setIsExpanded}
                />
            </div>
        )
    }

    if (!name) return null

    return (
        <div data-testid={`Row__Connections__${item?.type}`}>
            <RowDiv>
                <ResultRow>
                    <BadgeAvatar
                        id={id}
                        alt={name}
                        offline={isOffline}
                        onClick={onOpenMenu}
                        broadcasting={isBroadcasting}
                        style={{ marginRight: 8 }}
                        size={AVATAR_SIZE}
                        ref={avatarRef}
                        disableBadge
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                    />
                    <div
                        data-testid="Row__Connections_UserName"
                        style={{ color: modelColor }}
                        onClick={handleClick}
                    >
                        <Username
                            id={`name-${id}`}
                            data-testid="Container__UserName"
                        >
                            {name}
                        </Username>
                        {isModel && !isLounge && (
                            <BroadcastStatus>
                                {broadcastStateText({
                                    isTruePrivate,
                                    isBroadcasting,
                                    isPrivateShow,
                                    isGroupShow,
                                    isClubShow,
                                    isOffline,
                                    isLounge,
                                    isAway,
                                })}
                            </BroadcastStatus>
                        )}
                    </div>
                </ResultRow>
                {unreadMessages > 0 && (
                    <MessageBubbleContainer>
                        <IconButton
                            css={messageBubbleStyles}
                            data-testid="Connections__PrivateMessagesIconButton"
                            // color="primary"
                            onClick={onOpenQuickReply}
                            // size="large"
                        >
                            <MessageIcon filled />
                        </IconButton>
                        <Notification
                            styleOverrides={notificationStyles}
                            value={unreadMessages}
                        />
                    </MessageBubbleContainer>
                )}
            </RowDiv>
        </div>
    )
})

const Connections = memo(() => {
    const routeSlug = useRouteSelector('slug')
    const inLounge = isLounge(routeSlug)
    const loungeSlug = inLounge ? routeSlug : undefined
    const contentHeight = useScreenStateSelector('contentHeight')
    const allMessages = usePMSelector('chats')
    const rooms = useRooms()
    const pinnedRooms = usePinnedRooms()
    const passiveMode = usePassive()

    const { cache, processMessages } = messageUtil
    const [messages, setMessages] = useState<Array<IMessage>>([])
    const [unreadMessages, setUnread] = useState<Record<string, number>>({})

    useEffect(() => {
        if (!allMessages) return
        processMessages(allMessages)
        const { messages, unreadMessages } = cache
        setUnread(unreadMessages)
        setMessages(messages)
    }, [allMessages])

    const [openedRoomsSlugs, setOpenedRoomsSlugs] = useState(new Set<string>())

    const [openedRoomsExpanded, setOpenedRoomsExpanded] = useState(true)
    const [recentMessagesExpanded, setRecentMessagesExpanded] = useState(true)
    const [bookmarkedFriendsExpanded, setBookmarkedFriendsExpanded] =
        useState(true)
    const [modelFriendsExpanded, setModelFriendsExpanded] = useState(true)
    const [memberFriendsExpanded, setMemberFriendsExpanded] = useState(true)
    const [bookmarksExpanded, setBookmarksExpanded] = useState(true)

    const openedRooms = useMemo<Array<OpenRoom>>(() => {
        return Object.entries(rooms).reduce((acc, [roomId, roomState]) => {
            const id = Number(roomId)
            if (!roomState.viewing && !pinnedRooms.includes(id)) return acc
            return [
                ...acc,
                {
                    type: 'openRooms',
                    id,
                    unreadMessages: unreadMessages[idToSlug[id]] || 0,
                },
            ]
        }, [] as Array<OpenRoom>)
    }, [rooms, pinnedRooms, unreadMessages])

    useEffect(() => {
        const nextSlugs = openedRooms.map(({ id }) => idToSlug[id])
        if (!isEqual(nextSlugs, Array.from(openedRoomsSlugs))) {
            setOpenedRoomsSlugs(new Set(nextSlugs))
        }
    }, [openedRooms])

    const recentMessages = useMemo(
        () =>
            messages
                .reduce(
                    (acc, message) => {
                        const { unread, list = [], slug } = message
                        const filteredList = list.filter(
                            ({ news, system }) => !news && !system,
                        )
                        const { [filteredList.length - 1]: firstMessage = {} } =
                            filteredList
                        const { timestamp: rawTimestamp = 0 } = firstMessage
                        const timestamp =
                            rawTimestamp === 0 ? 0 : msTimestamp(rawTimestamp)
                        const isRecent =
                            Date.now() - timestamp < RECENT_MESSAGE_THRESHOLD
                        const isOpenedRoom = openedRoomsSlugs.has(slug)
                        if (isOpenedRoom || !isRecent) return acc
                        return [...acc, { slug, timestamp, unread }]
                    },
                    [] as Array<{
                        slug: string
                        timestamp: number
                        unread: number
                    }>,
                )
                .sort((a, b) => b.timestamp - a.timestamp),
        [messages, openedRoomsSlugs],
    )

    const recentMessagesList = useMemo<Array<RecentMessage>>(
        () =>
            recentMessages.map(({ slug, unread }) => ({
                type: 'recentMessages',
                slug,
                unreadMessages: unread || 0,
            })),
        [recentMessages],
    )

    const bookmarkedFriends = useList(ListType.BOOKMARKED_FRIENDS_ONLINE, {
        returnIDs: true,
    })
    const modelFriends = useList(ListType.MODEL_FRIENDS_ONLINE, {
        returnIDs: true,
    })
    const memberFriends = useList(ListType.MEMBER_FRIENDS_ONLINE, {
        returnIDs: true,
    })
    const bookmarks = useList(ListType.BOOKMARKS_ONLINE, {
        returnIDs: true,
    })

    const bookmarkedFriendsList = useMemo(
        () =>
            bookmarkedFriends.map((id) => ({
                type: 'bookmarkedFriends' as const,
                id,
            })),
        [bookmarkedFriends],
    )
    const modelFriendsList = useMemo(
        () =>
            modelFriends.map((id) => ({
                type: 'modelFriends' as const,
                id,
            })),
        [modelFriends],
    )
    const memberFriendsList = useMemo(
        () =>
            memberFriends.map((id) => ({
                type: 'memberFriends' as const,
                id,
            })),
        [memberFriends],
    )
    const bookmarksList = useMemo(
        () =>
            bookmarks.map((id) => ({
                type: 'bookmarks' as const,
                id,
            })),
        [bookmarks],
    )

    const sections = useMemo(() => {
        const typeCheckedObject: Record<SectionType, Section> = {
            openRooms: {
                key: 'openRooms',
                list: openedRooms,
                length: openedRooms.length,
                isExpanded: openedRoomsExpanded,
                setIsExpanded: setOpenedRoomsExpanded,
            },
            recentMessages: {
                key: 'recentMessages',
                list: recentMessagesList,
                length: recentMessagesList.length,
                isExpanded: recentMessagesExpanded,
                setIsExpanded: setRecentMessagesExpanded,
            },
            bookmarkedFriends: {
                key: 'bookmarkedFriends',
                list: bookmarkedFriendsList,
                length: bookmarkedFriendsList.length,
                isExpanded: bookmarkedFriendsExpanded,
                setIsExpanded: setBookmarkedFriendsExpanded,
            },
            modelFriends: {
                key: 'modelFriends',
                list: modelFriendsList,
                length: modelFriendsList.length,
                isExpanded: modelFriendsExpanded,
                setIsExpanded: setModelFriendsExpanded,
            },
            memberFriends: {
                key: 'memberFriends',
                list: memberFriendsList,
                length: memberFriendsList.length,
                isExpanded: memberFriendsExpanded,
                setIsExpanded: setMemberFriendsExpanded,
            },
            bookmarks: {
                key: 'bookmarks',
                list: bookmarksList,
                length: bookmarksList.length,
                isExpanded: bookmarksExpanded,
                setIsExpanded: setBookmarksExpanded,
            },
        }
        return typeCheckedObject
    }, [
        openedRooms,
        recentMessagesList,
        bookmarkedFriendsList,
        modelFriendsList,
        memberFriendsList,
        bookmarksList,
        openedRoomsExpanded,
        recentMessagesExpanded,
        bookmarkedFriendsExpanded,
        modelFriendsExpanded,
        memberFriendsExpanded,
        bookmarksExpanded,
    ])

    const listLength = Object.values(sections).reduce(
        (acc, { length }) => acc + length,
        0,
    )

    const list = useMemo(() => {
        const l: List = []
        Object.keys(sections).forEach((key) => {
            const section = sections[key as SectionType]
            if (section.length) {
                l.push(section)
                if (section.isExpanded) {
                    l.push(...section.list)
                }
            }
        })
        return l
    }, [sections])

    const openMenu = useCallback((slug: string) => {
        const isMessagePage = AppRouter.route.visiblePage === 'message'
        const isRoomPage = AppRouter.route.visiblePage === 'room'
        openMenuDialog(slug, {
            hideNextPrev: true,
            room: isRoomPage,
            message: isMessagePage,
            transitionSource: TransitionTypes.SIDEBAR,
        })
    }, [])

    const setPresizedPreview = useCallback(
        (show: boolean, id: number, x: number, y: number) => {
            const { windowWidth, windowHeight } = getScreenState()
            const isMessagePage = AppRouter.route.visiblePage === 'message'
            const isRoomPage = AppRouter.route.visiblePage === 'room'
            if (isRoomPage || isMessagePage) return
            if (show) {
                const previewWidth = windowWidth / 2 - 100
                const minY = Math.min(
                    y - AVATAR_SIZE - 10,
                    windowHeight - (previewWidth * 9) / 16 - 130,
                )
                setFloatingPreview(
                    id,
                    true,
                    {
                        close: false,
                        sticky: false,
                        stickyHeader: false,
                        size: FloaterSize.Large,
                    },
                    {
                        x: x - previewWidth - 10,
                        y: minY,
                        width: previewWidth,
                    },
                )
            } else {
                closeFloatingPreview(id)
            }
        },
        [],
    )

    const rows = useMemo(
        () =>
            list.map((item, index) => (
                <Row
                    key={itemKeySelector(index, item)}
                    item={item}
                    passiveMode={passiveMode}
                    loungeSlug={loungeSlug}
                    openMenu={openMenu}
                    setPreview={setPresizedPreview}
                />
            )),
        [list, passiveMode, loungeSlug, openMenu, setPresizedPreview],
    )

    if (listLength === 0) {
        return (
            <ConnectionsRoot>
                <span className="typography caption centered">
                    {i18n.t('friends.noModelOrMemberFriendsOnline')}
                </span>
            </ConnectionsRoot>
        )
    }

    return (
        <ConnectionsRoot id="sidebar-connections">
            <VList
                className="no-scrollbars"
                style={{ height: contentHeight, width: '100%' }}
                itemSize={ROW_HEIGHT}
            >
                {rows}
            </VList>
        </ConnectionsRoot>
    )
})

export default Connections
